import React, { useEffect, useState } from 'react';

import { Entity } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import infoTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Tag from '@amzn/meridian/tag';
import Tooltip from '@amzn/meridian/tooltip';

import { isEmpty } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { CommentSection } from 'src/components/Comment';
import { getUserElement } from 'src/components/RateCardHeader';
import { CommentCategory, Status } from 'src/constants';
import { CAM_ROLE_SET } from 'src/constants/role';
import { AppContext } from 'src/contexts/appContext';
import { mockContacts } from 'src/data/VendorContactsData';
import { IDealsRowData } from 'src/types/IDeals';
import { IVendorRowData } from 'src/types/IVendor';
import { getStage } from 'src/util/config';
import { getGeneralStatus } from 'src/util/grid/status';

import { ContactCard } from '../ContactCard';
import './VendorOverview.scss';

interface IVendorOverviewProps {
    vendor: Entity;
    deals: IDealsRowData[];
    vendorCodes: IVendorRowData[];
    dataLoaded: boolean;
}

export const VendorOverview = ({ vendor, deals, vendorCodes, dataLoaded }: IVendorOverviewProps) => {
    const [catalogCodeList, setCatalogCodeList] = useState<string[]>([]);
    const [royaltyCodeList, setRoyaltyCodeList] = useState<string[]>([]);
    const [activeServiceList, setActiveServiceList] = useState<string[]>([]);

    const { userRole } = React.useContext(AppContext);

    useEffect(() => {
        if (!isEmpty(vendorCodes)) {
            const catalogCodeSet = new Set<string>();
            const royaltyCodeSet = new Set<string>();

            vendorCodes.forEach((vendorCode) => {
                if (vendorCode.catalogVendorCode) catalogCodeSet.add(vendorCode.catalogVendorCode);
                if (vendorCode.royaltyVendorCode) royaltyCodeSet.add(vendorCode.royaltyVendorCode);
            });

            setCatalogCodeList(Array.from(catalogCodeSet));
            setRoyaltyCodeList(Array.from(royaltyCodeSet));
        }

        if (!isEmpty(deals)) {
            const activeServiceSet = new Set<string>();

            deals.forEach((deal) => {
                if (getGeneralStatus(deal) === Status.Active) {
                    activeServiceSet.add(deal.serviceName);
                }
            });

            setActiveServiceList(Array.from(activeServiceSet));
        }
    }, [deals, vendorCodes]);

    return (
        <Row width="100%" widths="fill" alignmentVertical="stretch">
            <Column height="100%" heights="fit" overflowY="scroll" alignmentHorizontal="stretch">
                <Box type="outline" spacingInset="400">
                    <Heading level={4}>Vendor Details</Heading>
                    <Table className="vendor-details-table">
                        <TableRow>
                            <TableCell>Partner Type</TableCell>
                            <TableCell>
                                {dataLoaded ? (vendor.entityType?.entityTypeName ?? '-') : <Skeleton width={180} />}
                            </TableCell>
                        </TableRow>
                        {getStage() === 'personal' && (
                            <TableRow>
                                <TableCell>Partner Sub Type</TableCell>
                                <TableCell>{dataLoaded ? '-' : <Skeleton width={180} />}</TableCell>
                            </TableRow>
                        )}
                        {getStage() === 'personal' && (
                            <TableRow>
                                <TableCell>Market Segment Share (MSS)</TableCell>
                                <TableCell>{dataLoaded ? '-' : <Skeleton width={180} />}</TableCell>
                            </TableRow>
                        )}
                        {getStage() === 'personal' && (
                            <TableRow>
                                <TableCell>Affiliates</TableCell>
                                <TableCell>{dataLoaded ? '-' : <Skeleton width={180} />}</TableCell>
                            </TableRow>
                        )}
                        {getStage() === 'personal' && (
                            <TableRow>
                                <TableCell>Aliases</TableCell>
                                <TableCell>{dataLoaded ? '-' : <Skeleton width={180} />}</TableCell>
                            </TableRow>
                        )}
                        {getStage() === 'personal' && (
                            <TableRow>
                                <TableCell>Distributor</TableCell>
                                <TableCell>{dataLoaded ? '-' : <Skeleton width={180} />}</TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TableCell>Royalties Deal Status</TableCell>
                            <TableCell>
                                {dataLoaded ? (
                                    activeServiceList[0] === '' ? (
                                        '—'
                                    ) : (
                                        activeServiceList
                                            .sort((a, b) => b.localeCompare(a))
                                            .map((service: string) => {
                                                return (
                                                    <>
                                                        <Tag type="success" key={service}>
                                                            {service}
                                                        </Tag>
                                                        <> </>
                                                    </>
                                                );
                                            })
                                    )
                                ) : (
                                    <Skeleton width={180} />
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Catalog Code</TableCell>
                            <TableCell>
                                {dataLoaded ? catalogCodeList.sort().join(', ') : <Skeleton width={180} />}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Royalty Code</TableCell>
                            <TableCell>
                                {dataLoaded ? royaltyCodeList.sort().join(', ') : <Skeleton width={180} />}
                            </TableCell>
                        </TableRow>
                    </Table>
                </Box>
                {getStage() === 'personal' && (
                    <>
                        <Box type="outline" spacingInset="400">
                            <Row spacing="200">
                                <Heading level={4}>Internal Details</Heading>
                                <Tooltip title="This section uses mock data" position="end">
                                    <Icon tokens={infoTokens} />
                                </Tooltip>
                            </Row>
                            <Table className="vendor-internal-contact-table">
                                <TableRow>
                                    <TableCell>Content Acquisition Contact</TableCell>
                                    <TableCell>
                                        {dataLoaded ? getUserElement('annsiege', false) : <Skeleton width={150} />}
                                    </TableCell>
                                </TableRow>
                            </Table>
                        </Box>
                        <Box type="outline" spacingInset="400">
                            <Column>
                                <Row spacing="200">
                                    <Heading level={4}>Primary Contacts</Heading>
                                    <Tooltip title="This section uses mock data" position="end">
                                        <Icon tokens={infoTokens} />
                                    </Tooltip>
                                </Row>
                                {dataLoaded ? (
                                    mockContacts
                                        .filter((contact) => contact.isPrimary)
                                        .map((contact) => <ContactCard key={contact.id} contact={contact} />)
                                ) : (
                                    <Column spacing="200">
                                        <Skeleton height={80} />
                                        <Skeleton height={80} />
                                    </Column>
                                )}
                            </Column>
                        </Box>
                    </>
                )}
            </Column>
            <Box type="outline" spacingInset="400">
                {vendor.entityId && (
                    <CommentSection
                        category={CommentCategory.Vendorcodes}
                        categoryUniqueColumnId={vendor.entityId}
                        title="Notes"
                        inputButtonText="Add Note"
                        inputPlaceholder="Add a note about this vendor..."
                        disableInput={!CAM_ROLE_SET.has(userRole)}
                        searchPlaceholder="Search notes..."
                        emptyStateMessage="No notes yet"
                        noResultsMessage="No notes match your search"
                    />
                )}
            </Box>
        </Row>
    );
};
